import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    AcademicCapIcon, 
    CalendarIcon, 
    ClockIcon, 
    UserGroupIcon,
    BuildingLibraryIcon,
    BookOpenIcon
} from '@heroicons/react/24/outline';

interface ProgramCardProps {
    id: string;
    type: 'program' | 'class' | 'course';  // 卡片类型
    title: string;
    subtitle: string;
    description: string;
    descriptionEn: string;
    image?: string;
    category?: string;
    status: 'active' | 'inactive' | 'upcoming';
    extraInfo?: {
        icon: 'academic' | 'calendar' | 'clock' | 'users' | 'building' | 'book';
        text: string;
        textEn: string;
    };
    disableNav?: boolean;
}

const ProgramCard: React.FC<ProgramCardProps> = ({
    id,
    type,
    title,
    subtitle,
    description,
    descriptionEn,
    image,
    category,
    status,
    extraInfo,
    disableNav=false
}) => {
    const { t, i18n } = useTranslation();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // 根据当前语言获取显示内容
    const getLocalizedContent = (zh: string, en: string) => {
        return i18n.language === 'zh' ? zh : en;
    };

    // 根据类型获取跳转路径
    const getLink = () => {
        if (disableNav) {
            return '#';
        }
        switch (type) {
            case 'program':
                return `/program/${id}`;
            case 'class':
                return `/class/${id}`;
            case 'course':
                return `/course/${id}`;
            default:
                return '#';
        }
    };

    // 获取图标组件
    const getIcon = (iconName: string) => {
        switch (iconName) {
            case 'academic':
                return AcademicCapIcon;
            case 'calendar':
                return CalendarIcon;
            case 'clock':
                return ClockIcon;
            case 'users':
                return UserGroupIcon;
            case 'building':
                return BuildingLibraryIcon;
            case 'book':
                return BookOpenIcon;
            default:
                return AcademicCapIcon;
        }
    };

    // 获取分类标签颜色
    const getCategoryColor = (category: string) => {
        const categoryKey = category.toLowerCase().replace(/[^a-z\u4e00-\u9fff]/g, '');
        const colors: { [key: string]: string } = {
            // 英文版本
            'saturdayprogram': 'bg-gradient-to-r from-blue-500/70 to-blue-600/70 text-white border border-blue-400/50',
            'afterschoolprogram': 'bg-gradient-to-r from-yellow-500/70 to-yellow-600/70 text-white border border-yellow-400/50',
            'summercamp': 'bg-gradient-to-r from-green-500/70 to-green-600/70 text-white border border-green-400/50',
            'onlineprogram': 'bg-gradient-to-r from-purple-500/70 to-purple-600/70 text-white border border-purple-400/50',
            'volunteerprogram': 'bg-gradient-to-r from-red-500/70 to-red-600/70 text-white border border-red-400/50',
            // 中文版本
            '周末': 'bg-gradient-to-r from-blue-500/70 to-blue-600/70 text-white border border-blue-400/50',
            '课后': 'bg-gradient-to-r from-yellow-500/70 to-yellow-600/70 text-white border border-yellow-400/50',
            '夏令营': 'bg-gradient-to-r from-green-500/70 to-green-600/70 text-white border border-green-400/50',
            '网课': 'bg-gradient-to-r from-purple-500/70 to-purple-600/70 text-white border border-purple-400/50',
            '志愿者': 'bg-gradient-to-r from-red-500/70 to-red-600/70 text-white border border-red-400/50'
        };
        return colors[categoryKey] || 'bg-gradient-to-r from-gray-500 to-gray-600 text-white border border-gray-400/50';
    };

    // 解析分类字符串为数组
    const getCategoryTags = () => {
        if (!category) return [];
        return category.split(',').map(cat => cat.trim()).filter(cat => cat.length > 0);
    };

    const Icon = extraInfo?.icon ? getIcon(extraInfo.icon) : AcademicCapIcon;

    return (
        <Link 
            to={getLink()}
            className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
        >
            {/* 分类标签 */}
            <div className="absolute top-3 left-3 z-10">
                <div className="flex items-center gap-1 max-w-[calc(100%-6rem)]">
                    {getCategoryTags().slice(0, 2).map((cat, index) => (
                        <span
                            key={index}
                            className={`text-xs px-2 py-1 rounded-md shadow-sm whitespace-nowrap font-medium ${getCategoryColor(cat)}`}
                        >
                            {cat}
                        </span>
                    ))}
                    {getCategoryTags().length > 2 && (
                        <span className="text-xs px-1.5 py-1 rounded-md shadow-sm bg-gradient-to-r from-gray-500 to-gray-600 text-white border border-gray-400/50 whitespace-nowrap font-medium">
                            +{getCategoryTags().length - 2}
                        </span>
                    )}
                </div>
            </div>

            {/* 状态标签 */}
            <div className="absolute top-3 right-3 z-10">
                <span className={`text-sm px-2 py-1 rounded-full shadow-md ${
                    status === 'active' 
                        ? 'bg-green-500 text-white' 
                        : status === 'upcoming'
                            ? 'bg-yellow-500 text-white'
                            : 'bg-gray-500 text-white'
                }`}>
                    {t(`programCard.status.${status}`)}
                </span>
            </div>

            <div className="aspect-w-16 aspect-h-9 relative bg-gray-200">
                {/* 图片加载占位符 */}
                {!imageLoaded && !imageError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <div className="animate-pulse flex space-x-4">
                            <div className="w-full h-48 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                )}
                
                {/* 图片 */}
                {image && !imageError ? (
                    <img
                        src={image}
                        alt={getLocalizedContent(title, subtitle)}
                        className={`object-cover w-full h-48 group-hover:scale-105 transition-transform duration-300 ${
                            imageLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <Icon className="h-12 w-12 text-gray-400" />
                    </div>
                )}
                
                {/* 渐变遮罩 */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 p-6">
                {/* 标题 */}
                <h3 className="text-xl font-semibold text-white mb-1">
                    {getLocalizedContent(title, subtitle)}
                </h3>

                {/* 描述 */}
                <p className="text-sm text-white/90 line-clamp-2 transition-all duration-300">
                    {getLocalizedContent(description, descriptionEn)}
                </p>

                {/* 额外信息 */}
                {extraInfo && (
                    <div className="mt-3 flex items-center text-white/90">
                        <Icon className="h-4 w-4 mr-2" />
                        <span className="text-sm">
                            {getLocalizedContent(extraInfo.text, extraInfo.textEn)}
                        </span>
                    </div>
                )}
            </div>
        </Link>
    );
};

export default ProgramCard; 